import React, { useState, useEffect, useRef } from "react";

interface BannerProps {
  groupId: string;
}

export const Banner: React.FC<BannerProps> = ({ groupId }) => {
  const [viewBlockHtml, setViewBlockHtml] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const owlCarouselRef = useRef<any>(null);

  // Load banner data
  const loadBanner = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/group_page_banner/${groupId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch block HTML: ${response.status} ${response.statusText}`
        );
      }
      const html = await response.text();
      setViewBlockHtml(html);
    } catch (err: any) {
      console.error("Error fetching Drupal view block:", err);
      setError(err.message || "Greška pri učitavanju banera.");
      setViewBlockHtml("");
    } finally {
      setLoading(false);
    }
  };

  // Load banner on mount
  useEffect(() => {
    loadBanner();
  }, [groupId]);

  // Initialize Owl Carousel when viewBlockHtml is loaded
  useEffect(() => {
    if (
      typeof (window as any).jQuery !== "function" ||
      typeof (window as any).jQuery.fn.owlCarousel !== "function"
    ) {
      return; // jQuery or Owl Carousel not available
    }
    const $ = (window as any).jQuery;

    if (!loading && viewBlockHtml) {
      let tries = 0;
      const intervalId = setInterval(async () => {
        try {
          const $catalogViewBannerDiv = $(".catalog-view-banner"); // The outer div from React
          // Find .view-content within the HTML injected into the child of $catalogViewBannerDiv
          const $categoryBannerHolder =
            $catalogViewBannerDiv.find(".view-content");

          if (viewBlockHtml.search("<img") !== -1) {
            // Wait for .category-banner elements to be available in DOM
            let $categoryBanners =
              $categoryBannerHolder.find(".category-banner");
            let retryCount = 0;
            const maxRetries = 10;

            while ($categoryBanners.length === 0 && retryCount < maxRetries) {
              new Promise((resolve) => setTimeout(resolve, 500));
              $categoryBanners = $categoryBannerHolder.find(".category-banner");
              retryCount++;
            }

            // Destroy any existing carousel on this specific $categoryBannerHolder element
            // Check if owlCarousel was initialized on this element before trying to destroy
            if (
              owlCarouselRef.current &&
              owlCarouselRef.current.is($categoryBannerHolder) &&
              typeof $categoryBannerHolder.data("owl.carousel") !== "undefined"
            ) {
              // console.log("Destroying previous Owl Carousel on .view-content");
              $categoryBannerHolder.trigger("destroy.owl.carousel");
            }

            if ($categoryBanners.length > 1) {
              // console.log(
              //   "Initializing Owl Carousel on .view-content for Drupal block"
              // );
              // Ensure your arrow variables are defined or use HTML strings
              const arrow = '<span class="your-prev-arrow-class">&lt;</span>';
              const arrow_right =
                '<span class="your-next-arrow-class">&gt;</span>';

              $categoryBannerHolder.owlCarousel({
                loop: true,
                margin: 0,
                nav: true,
                dots: false,
                items: 1,
                drag: false,
                autoplay: true,
                navText: [arrow, arrow_right], // Using navText as nav is true
              });
              owlCarouselRef.current = $categoryBannerHolder; // Store the jQuery object that was initialized
            } else {
              // If not enough banners, ensure a previous carousel on this element (if any) is cleared
              if (
                owlCarouselRef.current &&
                owlCarouselRef.current.is($categoryBannerHolder)
              ) {
                owlCarouselRef.current = null; // Clear ref if no carousel is initialized/re-initialized
              }
              owlCarouselRef.current.trigger("refresh.owl.carousel");
              // console.log(
              //   "Not enough .category-banner elements to initialize Owl Carousel."
              // );
            }
            clearInterval(intervalId);
          } else {
            // console.log(
            //   ".view-content not found within the Drupal block HTML."
            // );
          }
        } catch (error) {
          console.error("Error initializing Owl Carousel:", error);
          tries++;
          if (tries > 10) {
            clearInterval(intervalId);
          }
        }
      }, 2000); // Adjusted timeout, can be tweaked
    } else if (!loading && !viewBlockHtml) {
      // If block HTML is cleared, and we had a ref, try to destroy
      if (
        owlCarouselRef.current &&
        typeof owlCarouselRef.current.trigger === "function" &&
        typeof owlCarouselRef.current.data("owl.carousel") !== "undefined"
      ) {
        owlCarouselRef.current.trigger("destroy.owl.carousel");
        owlCarouselRef.current = null;
      }
    }
  }, [viewBlockHtml, loading]);

  if (loading) {
    return <div className="text-center py-4">Učitavanje banera...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-600">
        Greška pri učitavanju banera: {error}
      </div>
    );
  }

  if (!viewBlockHtml) {
    return null;
  }

  return (
    <div className="catalog-view-banner overflow-hidden">
      <div className="owl-carousel owl-theme view-banner">
        <div dangerouslySetInnerHTML={{ __html: viewBlockHtml }} />
      </div>
    </div>
  );
};
